// Distance-measuring tool with wind-corrected ETE + fuel \u2014 ForeFlight-style.
//
// Wiley Anderson feedback (2026-05-09):
//   "I need a feature to measure distances by placing two fingers on
//    the map screen as well you would include time to travel that
//    distance as well as fuel burn based on the aircraft profile."
//
// Gesture model (matches ForeFlight Mobile):
//
//   TOUCH (iPad/iPhone):
//     1. Place two fingers on the map.
//     2. Hold ~400ms with both fingers roughly stationary (<10px slop
//        per finger). DURING this window, MapLibre's native pinch-zoom
//        is still enabled \u2014 so a normal pinch-to-zoom gesture flows
//        unhindered.
//     3. After the long-press timer fires, the ruler arms: pinch-zoom
//        is suppressed, the line appears between the two fingers, and
//        EITHER finger can move independently to adjust each end.
//     4. Lift either finger \u2014 ruler stays frozen on screen until
//        either two-finger long-press starts a new measurement or the
//        user taps anywhere with one finger to dismiss.
//     5. Soft 15ms haptic when the ruler arms, so the user feels the
//        moment pinch-zoom switches off.
//
//   DESKTOP (mouse):
//     1. Click the ruler FAB on the right rail to enter ruler mode
//        (banner appears top-center). FAB lights cyan.
//     2. Click + drag anywhere on the map to draw the ruler.
//     3. Release \u2014 ruler stays frozen.
//     4. Click-drag again to redraw, or click the FAB / banner-X to
//        exit ruler mode.
//
// Visuals:
//   - Cyan line, 50% opacity (transparent), 3px wide, dashed.
//   - Cyan dots at both endpoints.
//   - Two readout pills, one anchored at each endpoint, showing the
//     direction-of-travel from THAT end:
//       * "from A to B": distance + TC + MH + GS + ETE + fuel + wind
//       * "from B to A": same, with reversed course; wind delta sign
//         flips automatically through the wind triangle.
//
// Wind correction:
//   sampleWindsAlongRoute(from\u2192to, alt) gives one vector-averaged
//   WindAloft for the line. windTriangle() turns that into GS + WCA
//   for both directions (the function is symmetric in TC, so we just
//   call it twice with the two reciprocal courses).

import { useEffect, useRef, useState } from 'react';
import type { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import { getMapInstance } from './MapView';
import { greatCircleNm, bearingDeg } from '../lib/geo';
import { sampleWindsAlongRoute } from '../lib/winds';
import { windTriangle, trueToMagnetic, type WindAloft } from '../lib/flightCalc';
import { useAircraftStore, activeCruise, selectActiveAircraft } from '../stores/aircraftStore';

interface RulerEnd {
  lon: number;
  lat: number;
  /** Last screen-space position of this end (for placing the readout pill). */
  screen: { x: number; y: number };
}

interface RulerState {
  from: RulerEnd | null;
  to:   RulerEnd | null;
  /** Optional cached wind for the current line. */
  wind: WindAloft | null;
  windsLoading: boolean;
}

const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] };
const RULER_SRC = 'ruler';
const RULER_HALO_LAYER   = 'ruler-halo';    // wide grey dim corridor (chart contrast)
const RULER_SWATH_LAYER  = 'ruler-swath';   // medium cyan accent band
const RULER_LINE_LAYER   = 'ruler-line';    // thin precise centerline
const RULER_DOTS_LAYER   = 'ruler-dots';    // tiny precision point markers

const TWO_FINGER_HOLD_MS = 400;
const TWO_FINGER_SLOP_PX = 10;

interface Props {
  /** Desktop FAB enables click-and-drag drawing. Touch path is always live. */
  desktopActive: boolean;
  /** Cruise altitude (ft MSL) used for the winds-aloft sample. Defaults to 6000ft. */
  cruiseAlt?: number | null;
  /** Time the wind sample should be valid for. Defaults to "now+30min". */
  time?: Date | string | null;
  /** Callback when desktop ruler mode should exit (e.g. user clicked the banner X). */
  onDesktopExit: () => void;
}

export default function Ruler({ desktopActive, cruiseAlt, time, onDesktopExit }: Props) {
  const aircraft = useAircraftStore(selectActiveAircraft);

  const stateRef = useRef<RulerState>({ from: null, to: null, wind: null, windsLoading: false });
  const [, setTick] = useState(0);
  const repaint = () => setTick((t) => t + 1);
  const state = stateRef.current;

  // ---- Map source/layer setup (deferred until style is ready) ------------
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    let cancelled = false;
    const ensureLayers = () => {
      if (cancelled) return;
      if (map.getSource(RULER_SRC)) return;
      map.addSource(RULER_SRC, { type: 'geojson', data: EMPTY_FC });
      // HALO — wide (60 px) dark corridor on either side of the centerline.
      // Dims the underlying VFR chart so the cyan centerline pops out against
      // an otherwise busy sectional. (Boss 2026-05-21 18:59 PDT.)
      map.addLayer({
        id: RULER_HALO_LAYER,
        type: 'line',
        source: RULER_SRC,
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-color': '#0b0d0f',     // near-black, matches cockpit chrome
          'line-width': 60,
          'line-opacity': 0.28,
          'line-blur': 8,              // soft edges — reads as a 'mist' not a bar
        },
      });
      // SWATH — medium-width cyan band (16 px, 18 % opacity). Sits over the
      // halo as an accent so the ruler retains its color identity.
      map.addLayer({
        id: RULER_SWATH_LAYER,
        type: 'line',
        source: RULER_SRC,
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-color': '#22d3ee',
          'line-width': 16,
          'line-opacity': 0.18,
          'line-blur': 0.5,
        },
      });
      // CENTERLINE — thin, solid, semi-transparent line on top of the swath
      // for precise reading.
      map.addLayer({
        id: RULER_LINE_LAYER,
        type: 'line',
        source: RULER_SRC,
        filter: ['==', '$type', 'LineString'],
        paint: {
          'line-color': '#22d3ee',
          'line-width': 1.5,
          'line-opacity': 0.75,
        },
      });
      // DOTS — small precise point markers at exact endpoints.
      map.addLayer({
        id: RULER_DOTS_LAYER,
        type: 'circle',
        source: RULER_SRC,
        filter: ['==', '$type', 'Point'],
        paint: {
          'circle-radius': 4,
          'circle-color': '#22d3ee',
          'circle-stroke-color': 'rgba(11, 13, 15, 0.9)',
          'circle-stroke-width': 1.5,
        },
      });
    };
    if (map.isStyleLoaded()) {
      ensureLayers();
    } else {
      // 'styledata' fires on every style mutation including the
      // initial load, AND fires on basemap-toggle setStyle() calls,
      // so this handler keeps the ruler layer alive across imagery /
      // chart switches that wipe the style. ensureLayers() is
      // idempotent (early-returns when the source already exists)
      // so multiple fires are safe.
      map.on('styledata', ensureLayers);
    }
    return () => {
      cancelled = true;
      map.off('styledata', ensureLayers);
    };
  }, []);

  // ---- Touch path: two-finger long-press, ALWAYS live ---------------------
  //
  // We don't gate this on a mode toggle \u2014 the gesture itself is the
  // activation. Pinch-zoom is left enabled UNTIL the long-press timer
  // fires, at which point we yank touchZoomRotate.disable() so the
  // ruler owns the gesture cleanly.
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    const canvas = map.getCanvasContainer();

    let timer: number | null = null;
    let armed = false;
    let starts: { x: number; y: number }[] = [];
    let frozen = false; // true once user lifted; ruler line stays on screen

    const clearTimer = () => { if (timer != null) { window.clearTimeout(timer); timer = null; } };
    const arm = () => {
      armed = true;
      frozen = false;
      // Suspend pinch-zoom and dragPan while the gesture is owned by
      // the ruler. Re-enabled on touchend.
      try { map.touchZoomRotate.disable(); } catch {}
      try { map.dragPan.disable(); } catch {}
      try { (navigator as Navigator & { vibrate?: (ms: number) => void }).vibrate?.(15); } catch {}
    };
    const release = () => {
      armed = false;
      try { map.touchZoomRotate.enable(); } catch {}
      try { map.dragPan.enable(); } catch {}
    };
    const reset = () => {
      clearTimer();
      armed = false;
      starts = [];
      release();
    };

    const onTouchStart = (ev: TouchEvent) => {
      // Single-finger tap on the canvas while the ruler is frozen
      // dismisses the ruler (matches ForeFlight \u2014 tap-anywhere clears).
      if (frozen && ev.touches.length === 1) {
        const map = getMapInstance();
        if (map) {
          const src = map.getSource(RULER_SRC) as GeoJSONSource | undefined;
          if (src) src.setData(EMPTY_FC);
        }
        stateRef.current = { from: null, to: null, wind: null, windsLoading: false };
        frozen = false;
        repaint();
        return;
      }
      if (ev.touches.length !== 2) { reset(); return; }
      // Capture the two starting positions in canvas coordinates so we
      // can detect the slop budget without depending on map projection.
      const rect = canvas.getBoundingClientRect();
      starts = [
        { x: ev.touches[0].clientX - rect.left, y: ev.touches[0].clientY - rect.top },
        { x: ev.touches[1].clientX - rect.left, y: ev.touches[1].clientY - rect.top },
      ];
      clearTimer();
      timer = window.setTimeout(() => {
        timer = null;
        // If we got here, both fingers were stationary long enough.
        // Arm the ruler and paint immediately so the user sees a line
        // appear under their fingers.
        arm();
        // Snap the ruler to the CURRENT finger positions \u2014 not the
        // starts we recorded \u2014 so the visual feels stuck to the
        // fingertips. (They're typically within slop of starts but
        // the small drift looks bad if we ignore it.)
        applyTouches(ev);
        // Ensure the next touchmove with cancelable=true wins by
        // calling preventDefault eagerly.
      }, TWO_FINGER_HOLD_MS);
    };

    const onTouchMove = (ev: TouchEvent) => {
      if (ev.touches.length !== 2) return;
      const rect = canvas.getBoundingClientRect();
      const cur = [
        { x: ev.touches[0].clientX - rect.left, y: ev.touches[0].clientY - rect.top },
        { x: ev.touches[1].clientX - rect.left, y: ev.touches[1].clientY - rect.top },
      ];
      if (!armed) {
        // Pre-arm: cancel the timer if either finger moved past slop.
        // This is what lets pinch-zoom flow normally \u2014 movement during
        // the hold window means the user is pinching, not measuring.
        if (starts.length === 2) {
          const d0 = Math.hypot(cur[0].x - starts[0].x, cur[0].y - starts[0].y);
          const d1 = Math.hypot(cur[1].x - starts[1].x, cur[1].y - starts[1].y);
          if (d0 > TWO_FINGER_SLOP_PX || d1 > TWO_FINGER_SLOP_PX) reset();
        }
        return;
      }
      // Armed: live-track both fingers independently.
      applyTouches(ev);
      if (ev.cancelable) ev.preventDefault();
    };

    const applyTouches = (ev: TouchEvent) => {
      const rect = canvas.getBoundingClientRect();
      const t0 = ev.touches[0];
      const t1 = ev.touches[1];
      const p0 = { x: t0.clientX - rect.left, y: t0.clientY - rect.top };
      const p1 = { x: t1.clientX - rect.left, y: t1.clientY - rect.top };
      const ll0 = map.unproject([p0.x, p0.y]);
      const ll1 = map.unproject([p1.x, p1.y]);
      stateRef.current.from = { lon: ll0.lng, lat: ll0.lat, screen: p0 };
      stateRef.current.to   = { lon: ll1.lng, lat: ll1.lat, screen: p1 };
      paintLine(map, stateRef.current);
      repaint();
    };

    const onTouchEnd = (ev: TouchEvent) => {
      // If we were armed and a finger lifted, freeze the ruler at its
      // last position so the readouts stay visible.
      if (armed && stateRef.current.from && stateRef.current.to) {
        frozen = true;
        kickWindsFetch(stateRef.current, cruiseAlt ?? 6000, time, repaint);
      } else if (!armed) {
        // Wasn't armed (timer pending or already canceled). Nothing to do.
      }
      clearTimer();
      armed = false;
      starts = [];
      // Re-enable map gestures unconditionally on lift; even a frozen
      // ruler should not eat single-finger pan inputs.
      try { map.touchZoomRotate.enable(); } catch {}
      try { map.dragPan.enable(); } catch {}
      // Voiding ev to silence the TS unused warning when we don't read it.
      void ev;
    };

    canvas.addEventListener('touchstart', onTouchStart, { passive: true });
    canvas.addEventListener('touchmove',  onTouchMove,  { passive: false });
    canvas.addEventListener('touchend',   onTouchEnd,   { passive: true });
    canvas.addEventListener('touchcancel', onTouchEnd,  { passive: true });

    return () => {
      canvas.removeEventListener('touchstart', onTouchStart);
      canvas.removeEventListener('touchmove',  onTouchMove);
      canvas.removeEventListener('touchend',   onTouchEnd);
      canvas.removeEventListener('touchcancel', onTouchEnd);
      reset();
    };
    // cruiseAlt + time are captured by closure for the windsfetch on
    // freeze; if either changes mid-gesture we accept the slight
    // staleness rather than re-binding listeners (which would drop
    // an in-progress measurement).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Desktop path: click+drag while desktopActive=true -----------------
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;
    if (!desktopActive) return;

    const canvas = map.getCanvasContainer();
    let dragging = false;

    const onMouseDown = (ev: MouseEvent) => {
      if (ev.button !== 0) return;
      const rect = canvas.getBoundingClientRect();
      const point = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      const lngLat = map.unproject([point.x, point.y]);
      stateRef.current.from = { lon: lngLat.lng, lat: lngLat.lat, screen: point };
      stateRef.current.to   = { lon: lngLat.lng, lat: lngLat.lat, screen: point };
      dragging = true;
      try { map.dragPan.disable(); } catch {}
      paintLine(map, stateRef.current);
      ev.preventDefault();
    };
    const onMouseMove = (ev: MouseEvent) => {
      if (!dragging) return;
      const rect = canvas.getBoundingClientRect();
      const point = { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
      const lngLat = map.unproject([point.x, point.y]);
      stateRef.current.to = { lon: lngLat.lng, lat: lngLat.lat, screen: point };
      paintLine(map, stateRef.current);
      repaint();
    };
    const onMouseUp = () => {
      if (!dragging) return;
      dragging = false;
      try { map.dragPan.enable(); } catch {}
      kickWindsFetch(stateRef.current, cruiseAlt ?? 6000, time, repaint);
      repaint();
    };

    canvas.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    canvas.style.cursor = 'crosshair';

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
      canvas.style.cursor = '';
      try { map.dragPan.enable(); } catch {}
    };
  }, [desktopActive, cruiseAlt, time]);

  // ---- Render the readout pills ------------------------------------------
  const { from, to, wind, windsLoading } = state;
  const distNm = (from && to) ? greatCircleNm(from.lat, from.lon, to.lat, to.lon) : 0;
  const tcAB = (from && to && distNm > 0.05) ? bearingDeg(from.lat, from.lon, to.lat, to.lon) : null;
  const tcBA = tcAB == null ? null : (tcAB + 180) % 360;
  const { cruiseKt, fuelBurnGph } = activeCruise(aircraft);

  const fwdPerf = perfFor(distNm, tcAB,  cruiseKt, fuelBurnGph, wind);
  const revPerf = perfFor(distNm, tcBA,  cruiseKt, fuelBurnGph, wind);

  // Show banner only on desktop (touch path is gestural; banner would
  // be confusing because there's no mode to be "in" between gestures).
  const showBanner = desktopActive;

  return (
    <>
      {showBanner && (
        <div className="absolute z-30 top-2 left-1/2 -translate-x-1/2 bg-cockpit-800/95 border border-cockpit-600 rounded-md px-3 py-1.5 text-xs text-cockpit-100 backdrop-blur shadow flex items-center gap-3">
          <span className="text-cyan-300">Ruler mode</span>
          <span className="text-cockpit-400">click-drag on map</span>
          <button
            onClick={onDesktopExit}
            className="underline text-cockpit-300 hover:text-cockpit-100"
          >Exit</button>
        </div>
      )}

      {/* Endpoint markers — perpendicular tick + arrowhead in screen space.
          These sit at the precise endpoint so the user can SEE the
          measured points even when fingers cover the screen position. */}
      {from && to && distNm > 0.05 && tcAB != null && (
        <>
          <EndpointMark screen={from.screen} bearingDeg={tcAB} />
          <EndpointMark screen={to.screen} bearingDeg={(tcAB + 180) % 360} />
        </>
      )}

      {/* From-end readout */}
      {from && to && distNm > 0.05 && (
        <ReadoutPill
          screen={from.screen}
          align="from"
          label={`TO ${formatHeading(tcAB)}\u00b0T`}
          distNm={distNm}
          perf={fwdPerf}
          windsLoading={windsLoading}
          hasAircraft={!!cruiseKt}
        />
      )}
      {from && to && distNm > 0.05 && (
        <ReadoutPill
          screen={to.screen}
          align="to"
          label={`TO ${formatHeading(tcBA)}\u00b0T`}
          distNm={distNm}
          perf={revPerf}
          windsLoading={windsLoading}
          hasAircraft={!!cruiseKt}
        />
      )}
    </>
  );
}

// ---- Subcomponents -------------------------------------------------------

interface PerfCalc {
  gsKt: number | null;
  mhDeg: number | null;
  eteMin: number | null;
  fuelGal: number | null;
  /** Positive = headwind, negative = tailwind. */
  headwindKt: number | null;
}

function perfFor(
  distNm: number,
  tcDeg: number | null,
  cruiseKt: number | undefined,
  fuelBurnGph: number | undefined,
  wind: WindAloft | null,
): PerfCalc {
  if (!cruiseKt || tcDeg == null) {
    return { gsKt: null, mhDeg: null, eteMin: null, fuelGal: null, headwindKt: null };
  }
  const wt = windTriangle(tcDeg, cruiseKt, wind);
  const gs = Number.isFinite(wt.groundspeedKt) && wt.groundspeedKt > 0 ? wt.groundspeedKt : cruiseKt;
  const headingTrue = Number.isFinite(wt.trueHeadingDeg) ? wt.trueHeadingDeg : tcDeg;
  const mh = trueToMagnetic(headingTrue, 15);
  const eteMin = (distNm / gs) * 60;
  const fuelGal = fuelBurnGph ? (eteMin / 60) * fuelBurnGph : null;
  const headwindKt = cruiseKt - gs;
  return { gsKt: gs, mhDeg: mh, eteMin, fuelGal, headwindKt };
}

function ReadoutPill({
  screen, align, label, distNm, perf, windsLoading, hasAircraft,
}: {
  screen: { x: number; y: number };
  align: 'from' | 'to';
  label: string;
  distNm: number;
  perf: PerfCalc;
  windsLoading: boolean;
  hasAircraft: boolean;
}) {
  // Position the pill 14px away from the endpoint, offset toward the
  // screen interior so the pill doesn't get clipped at edges. We pick
  // up/down + left/right based on which screen quadrant the endpoint
  // sits in.
  const w = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const h = typeof window !== 'undefined' ? window.innerHeight : 800;
  const offsetX = screen.x < w / 2 ? 16 : -16;
  const offsetY = screen.y < h / 2 ? 16 : -16;
  const transform = `translate(${screen.x + offsetX}px, ${screen.y + offsetY}px) translate(${offsetX > 0 ? '0' : '-100%'}, ${offsetY > 0 ? '0' : '-100%'})`;

  return (
    <div
      className="absolute z-30 pointer-events-none bg-cockpit-800/85 border border-cyan-500/50 rounded-md px-2.5 py-1.5 text-[11px] text-cockpit-100 backdrop-blur shadow font-mono whitespace-nowrap"
      style={{ left: 0, top: 0, transform }}
      data-align={align}
    >
      <div className="text-cyan-300 font-bold">
        {distNm.toFixed(1)} nm <span className="text-cockpit-300 font-normal">{label}</span>
      </div>
      {perf.gsKt != null ? (
        <div className="text-cockpit-200">
          MH {formatHeading(perf.mhDeg)}{'\u00b0'} &middot; GS {Math.round(perf.gsKt)}kt
          {perf.eteMin != null && <> &middot; {formatHm(perf.eteMin)}</>}
          {perf.fuelGal != null && <> &middot; {perf.fuelGal.toFixed(1)}gal</>}
          {perf.headwindKt != null && (
            <span className={perf.headwindKt > 0 ? ' text-amber-300' : perf.headwindKt < 0 ? ' text-emerald-300' : ''}>
              {' '}&middot; {perf.headwindKt > 0 ? '\u2191' : perf.headwindKt < 0 ? '\u2193' : ''}{Math.round(Math.abs(perf.headwindKt))}kt
            </span>
          )}
          {windsLoading && <span className="text-cockpit-400 italic"> &middot; {'winds\u2026'}</span>}
        </div>
      ) : (
        <div className="text-cockpit-400 italic">
          {hasAircraft ? '\u2014' : 'Set active aircraft for ETE/fuel'}
        </div>
      )}
    </div>
  );
}

/** Endpoint mark = perpendicular tick + chevron arrowhead drawn in CSS at
 *  the screen position of the endpoint. The arrowhead points along the
 *  bearing AWAY from this endpoint toward the other end, so the visual
 *  cue matches the heading shown on the pill.
 *
 *  Tick length = 32 px (16 either side of center) and the chevron sits
 *  ~14 px in the arrow direction, so the precise endpoint is clearly
 *  marked even when a finger is on top of it.
 *  Boss 2026-05-21 18:42 PDT. */
function EndpointMark({ screen, bearingDeg }: { screen: { x: number; y: number }; bearingDeg: number }) {
  // Bearing is in compass degrees (N=0, E=90, S=180, W=270). CSS rotate uses
  // clockwise from up. They happen to match, so we can use bearing directly.
  const tickStyle: React.CSSProperties = {
    left: 0, top: 0,
    transform: `translate(${screen.x}px, ${screen.y}px) translate(-50%, -50%) rotate(${bearingDeg}deg)`,
  };
  return (
    <div className="absolute z-30 pointer-events-none" style={tickStyle}>
      {/* Perpendicular tick (horizontal in the rotated frame; perpendicular to bearing in screen frame) */}
      <div
        style={{
          position: 'absolute', left: '-16px', top: '-1px',
          width: '32px', height: '2px',
          background: 'rgba(34, 211, 238, 0.9)',
          boxShadow: '0 0 0 1px rgba(11, 13, 15, 0.9)',
        }}
      />
      {/* Arrowhead pointing along the bearing direction (up in rotated frame) */}
      <div
        style={{
          position: 'absolute', left: '-6px', top: '-20px',
          width: 0, height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderBottom: '10px solid rgba(34, 211, 238, 0.95)',
          filter: 'drop-shadow(0 0 1.5px rgba(11, 13, 15, 0.9))',
        }}
      />
    </div>
  );
}

// ---- helpers -------------------------------------------------------------

function paintLine(map: MlMap, s: RulerState) {
  const src = map.getSource(RULER_SRC) as GeoJSONSource | undefined;
  if (!src) return;
  if (!s.from || !s.to) {
    src.setData(EMPTY_FC);
    return;
  }
  src.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: [[s.from.lon, s.from.lat], [s.to.lon, s.to.lat]] },
      },
      { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [s.from.lon, s.from.lat] } },
      { type: 'Feature', properties: {}, geometry: { type: 'Point', coordinates: [s.to.lon, s.to.lat] } },
    ],
  });
}

function kickWindsFetch(
  s: RulerState,
  altFt: number,
  time: Date | string | null | undefined,
  onDone: () => void,
) {
  if (!s.from || !s.to) return;
  s.windsLoading = true;
  sampleWindsAlongRoute([s.from, s.to], altFt, time)
    .then((winds) => {
      s.wind = winds[0] ?? null;
      s.windsLoading = false;
      onDone();
    })
    .catch(() => {
      s.windsLoading = false;
      onDone();
    });
  onDone();
}

function formatHm(min: number): string {
  if (!Number.isFinite(min)) return '\u2014';
  const h = Math.floor(min / 60);
  const m = Math.round(min - h * 60);
  if (h === 0) return `${m}m`;
  return `${h}h${m.toString().padStart(2, '0')}m`;
}

function formatHeading(deg: number | null): string {
  if (deg == null || !Number.isFinite(deg)) return '\u2014';
  return Math.round(deg).toString().padStart(3, '0');
}
